import { X, Trash2, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/lib/cart";

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_CHECKOUT_DOMAIN;
// After checkout completes, Shopify sends the shopper back to this URL.
const RETURN_TO = "https://rainboyfineart.com";

function buildShopifyCartUrl(items) {
  const withVariants = items.filter((i) => i.shopify_variant_id);
  if (withVariants.length === 0) return null;
  const pairs = withVariants.map((i) => `${i.shopify_variant_id}:1`).join(",");
  const returnParam = encodeURIComponent(RETURN_TO);
  return `https://${SHOPIFY_DOMAIN}/cart/${pairs}?return_to=${returnParam}`;
}

export function CartDrawer() {
  const { items, removeItem, clearCart, total, drawerOpen, setDrawerOpen } = useCart();

  const missingVariant = items.filter((i) => !i.shopify_variant_id);
  const canCheckout = items.length > 0 && missingVariant.length === 0 && Boolean(SHOPIFY_DOMAIN);

  const handleCheckout = () => {
    if (!SHOPIFY_DOMAIN) {
      alert("Checkout is temporarily unavailable. Please contact the gallery directly.");
      return;
    }
    if (missingVariant.length > 0) {
      const titles = missingVariant.map((i) => `"${i.title}"`).join(", ");
      alert(
        `${titles} can't be checked out online yet — please re-add them to your cart or contact us for purchase details.`
      );
      return;
    }
    const url = buildShopifyCartUrl(items);
    if (!url) return;
    // Same-tab redirect: shopper proceeds directly to Shopify checkout.
    window.location.href = url;
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-backdrop"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed top-0 right-0 bottom-0 z-[85] w-full max-w-md bg-white shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#0A1128]/10">
              <div className="flex items-center gap-3">
                <ShoppingBag size={18} className="text-[#0A1128]" />
                <h2 className="font-heading text-lg text-[#0A1128]">
                  Your Cart ({items.length})
                </h2>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="text-[#8A94A6] hover:text-[#0A1128] transition-colors"
                data-testid="cart-drawer-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center" data-testid="cart-empty">
                  <ShoppingBag size={40} className="text-[#d3cac0] mb-4" />
                  <p className="text-[#8A94A6] text-sm mb-1">Your cart is empty</p>
                  <p className="text-[#8A94A6] text-xs">Browse our collection to find your next piece</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 py-4 border-b border-[#0A1128]/5 last:border-0"
                      data-testid={`cart-item-${item.id}`}
                    >
                      <div className="w-20 h-24 flex-shrink-0 overflow-hidden bg-[#F8F9FA]">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-sm text-[#0A1128] truncate">{item.title}</h3>
                        <p className="text-xs text-[#8A94A6] mt-0.5">{item.artist_name}</p>
                        {item.dimensions && (
                          <p className="text-xs text-[#8A94A6] mt-0.5">{item.dimensions}</p>
                        )}
                        <p className="text-sm text-[#0A1128] mt-2 font-heading font-semibold">
                          ${item.price?.toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="self-start text-[#8A94A6] hover:text-red-500 transition-colors p-1"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-[#0A1128]/10 px-6 py-5 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#8A94A6]">Total</span>
                  <span className="font-heading font-semibold text-xl text-[#0A1128]" data-testid="cart-total">
                    ${total.toLocaleString()}
                  </span>
                </div>
                <button
                  className="btn-fill-hover btn-fill-dark w-full bg-[#0A1128] text-white py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="cart-checkout-button"
                  onClick={handleCheckout}
                  disabled={!canCheckout}
                >
                  Checkout
                </button>
                {missingVariant.length > 0 && (
                  <p
                    className="text-[11px] text-[#B36B00] leading-snug"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    Some items aren't yet available for online checkout. Please contact the gallery directly for those pieces.
                  </p>
                )}
                <button
                  onClick={clearCart}
                  className="w-full text-xs text-[#8A94A6] hover:text-[#0A1128] transition-colors uppercase tracking-[0.15em] py-2"
                  data-testid="cart-clear-button"
                >
                  Clear Cart
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
